<template>
    <div class="story-viewer-overlay"
         @touchstart="onTouchStart"
         @touchend="onTouchEnd"
         @mousedown="onMouseDown"
         @mouseup="onMouseUp"
         @mouseleave="onMouseUp"
         @contextmenu.prevent>

        <div class="story-viewer"
             :class="{
                 'story-slide-out-left': transitionPhase === 'out' && transitionDirection === 'next',
                 'story-slide-out-right': transitionPhase === 'out' && transitionDirection === 'prev',
                 'story-slide-in-left': transitionPhase === 'in' && transitionDirection === 'next',
                 'story-slide-in-right': transitionPhase === 'in' && transitionDirection === 'prev',
             }">

            <div class="story-viewer-progress">
                <div v-for="(item, index) in currentStory.items"
                     :key="item.id"
                     class="story-viewer-progress-bar">
                    <div class="story-viewer-progress-fill"
                         :style="{
                             width: index < currentItemIndex ? '100%' :
                                    index === currentItemIndex ? progress + '%' : '0%'
                         }">
                    </div>
                </div>
            </div>

            <div class="story-viewer-header">
                <a v-if="currentStory.profileUrl"
                   :href="currentStory.profileUrl"
                   target="_blank"
                   class="story-viewer-profile"
                   @click.stop>
                    <img :src="currentStory.photo"
                         :alt="currentStory.name"
                         class="story-viewer-header-avatar" />
                    <span class="story-viewer-name">{{ currentStory.name }}</span>
                </a>
                <div v-else class="story-viewer-profile" @click.stop>
                    <img :src="currentStory.photo"
                         :alt="currentStory.name"
                         class="story-viewer-header-avatar" />
                    <span class="story-viewer-name">{{ currentStory.name }}</span>
                </div>
                <div class="story-viewer-actions">
                    <button @click.stop="togglePause" class="story-viewer-btn">
                        <svg v-if="isPaused || isHolding" fill="currentColor" viewBox="0 0 24 24" width="24" height="24">
                            <path d="M8 5v14l11-7z"/>
                        </svg>
                        <svg v-else fill="currentColor" viewBox="0 0 24 24" width="24" height="24">
                            <path d="M7 4h3v16H7zM14 4h3v16h-3z"/>
                        </svg>
                    </button>
                    <button v-if="currentItem && currentItem.type === 'video'"
                            @click.stop="toggleMute"
                            class="story-viewer-btn">
                        <svg v-if="isMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/>
                        </svg>
                        <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/>
                        </svg>
                    </button>
                    <button @click.stop="close" class="story-viewer-btn">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
            </div>

            <div class="story-viewer-content">
                <template v-if="currentItem && currentItem.type === 'video'">
                    <div class="story-viewer-video-container">
                        <div class="story-viewer-video-thumbnail-bg"
                             :style="{ backgroundImage: 'url(' + currentItem.thumbnail + ')' }">
                        </div>
                        <img v-if="currentItem.width && currentItem.height"
                             class="story-viewer-video-thumbnail-sized"
                             :src="currentItem.thumbnail"
                             alt="" />
                        <video ref="videoEl"
                               :key="currentItem.id"
                               class="story-viewer-video"
                               :style="{ opacity: videoReady ? 1 : 0 }"
                               playsinline
                               muted
                               preload="auto"
                               @timeupdate="onVideoTimeUpdate"
                               @ended="onVideoEnded"
                               @playing="onVideoPlaying">
                        </video>
                    </div>
                </template>
                <img v-else-if="currentItem"
                     :src="currentItem.src"
                     :alt="currentStory.name"
                     class="story-viewer-image" />
            </div>

            <div class="story-viewer-nav story-viewer-nav-prev"
                 @click.stop="handleNavClick('prev')"></div>
            <div class="story-viewer-nav story-viewer-nav-next"
                 @click.stop="handleNavClick('next')"></div>
        </div>

        <button class="story-nav-arrow story-nav-arrow-prev"
                @click.stop="goToPreviousItem">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="28" height="28">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
        </button>
        <button class="story-nav-arrow story-nav-arrow-next"
                @click.stop="goToNextItem">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="28" height="28">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
        </button>
    </div>
</template>

<script>
import Hls from 'hls.js';
import userMixin from '@/components/mixins/userMixin';
import xtrackingMixin from '@/components/mixins/xtrackingMixin';

const data = function () {
    return {
        currentStoryIndex: this.initialStoryIndex,
        currentItemIndex: 0,
        progress: 0,
        isMuted: true,
        isPaused: false,
        isHolding: false,
        isTransitioning: false,
        transitionPhase: null,
        transitionDirection: null,
        incomingStoryIndex: null,
        touchStartX: 0,
        touchStartY: 0,
        holdTimeout: null,
        lastHoldReleaseTime: 0,
        progressInterval: null,
        hlsInstance: null,
        imageProgressStartTime: 0,
        imageProgressPausedAt: 0,
        preloadedVideos: {},
        preloadedHlsInstances: {},
        preloadedImages: {},
        videoReady: false,
    };
};

function mounted() {
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    this.trackStoryView(this.currentStoryIndex);
    this.$nextTick(() => {
        this.playCurrentItem();
    });
}

function beforeDestroy() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onKeydown);
    this.stopProgress();
    this.destroyHls();
    this.destroyAllPreloaded();

    if (this.holdTimeout) {
        clearTimeout(this.holdTimeout);
    }
}

function close() {
    this.$emit('close');
}

function trackStoryView(storyIndex) {
    const story = this.stories[storyIndex];
    if (!story) {
        return;
    }

    this.captureTrackingEvent(
        this.currentUser,
        'search',
        'stories',
        'view',
        null,
        {storyId: story.storyId, storyUserId: story.userId, position: storyIndex + 1}
    );
}

function destroyHls() {
    if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
    }
}

function destroyAllPreloaded() {
    Object.values(this.preloadedHlsInstances).forEach(hls => {
        if (hls) {
            hls.destroy();
        }
    });
    this.preloadedHlsInstances = {};

    Object.values(this.preloadedVideos).forEach(video => {
        if (video) {
            video.pause();
            video.src = '';
            video.load();
        }
    });
    this.preloadedVideos = {};
    this.preloadedImages = {};
}

function preloadStory(storyIndex) {
    const story = this.stories[storyIndex];
    if (!story || !story.items) {
        return;
    }

    story.items.forEach(item => {
        if (this.preloadedVideos[item.id] || this.preloadedImages[item.id]) {
            return;
        }

        if (item.type === 'video' && item.src) {
            const video = document.createElement('video');
            video.preload = 'auto';
            video.muted = true;

            const isHlsStream = item.isAdaptive && item.src.includes('.m3u8');
            if (isHlsStream && Hls.isSupported()) {
                const hls = new Hls({enableWorker: true, lowLatencyMode: false});
                hls.loadSource(item.src);
                hls.attachMedia(video);
                this.preloadedHlsInstances[item.id] = hls;
            } else {
                video.src = item.src;
            }

            this.preloadedVideos[item.id] = video;
        } else if (item.type === 'photo' && item.src) {
            const img = new Image();
            img.src = item.src;
            this.preloadedImages[item.id] = img;
        }
    });
}

function shouldSkipStory(storyIndex) {
    const story = this.stories[storyIndex];
    if (!story) {
        return true;
    }
    if (!story.items || story.items.length === 0) {
        return true;
    }
    if (story.isExplicit && this.storyHasPlaceholderItems(story)) {
        return true;
    }
    return false;
}

function storyHasPlaceholderItems(story) {
    return story.items.length === 1 && story.items[0].id.includes('_placeholder');
}

function findNextValidStoryIndex(fromIndex) {
    for (let i = fromIndex + 1; i < this.stories.length; i++) {
        if (!this.shouldSkipStory(i)) {
            return i;
        }
    }
    return -1;
}

function findPreviousValidStoryIndex(fromIndex) {
    for (let i = fromIndex - 1; i >= 0; i--) {
        if (!this.shouldSkipStory(i)) {
            return i;
        }
    }
    return -1;
}

function playCurrentItem() {
    this.stopProgress();
    this.progress = 0;
    this.videoReady = false;

    if (!this.currentItem) {
        return;
    }

    const isLastItem = this.currentItemIndex === this.totalItems - 1;
    if (isLastItem) {
        this.$emit('story-seen', this.currentStory.storyId);
    }

    const nextStoryIndex = this.findNextValidStoryIndex(this.currentStoryIndex);
    if (nextStoryIndex !== -1) {
        this.preloadStory(nextStoryIndex);
    }

    if (this.currentItem.type === 'video') {
        this.$nextTick(() => {
            this.initHlsPlayer();
        });
    } else {
        this.startImageProgress();
    }
}

function initHlsPlayer() {
    const video = this.$refs.videoEl;
    if (!video || !this.currentItem) {
        return;
    }

    this.destroyHls();

    const src = this.currentItem.src;
    const isHlsStream = this.currentItem.isAdaptive && src.includes('.m3u8');

    if (isHlsStream && Hls.isSupported()) {
        this.hlsInstance = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
        });
        this.hlsInstance.loadSource(src);
        this.hlsInstance.attachMedia(video);
        this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
            video.muted = this.isMuted;
            video.play().catch(() => {});
        });
        this.hlsInstance.on(Hls.Events.ERROR, (_, errorData) => {
            if (!errorData.fatal) {
                return;
            }
            if (errorData.type === Hls.ErrorTypes.NETWORK_ERROR) {
                this.hlsInstance.startLoad();
            } else if (errorData.type === Hls.ErrorTypes.MEDIA_ERROR) {
                this.hlsInstance.recoverMediaError();
            }
        });
        return;
    }

    if (isHlsStream && video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
        video.muted = this.isMuted;
        video.play().catch(() => {});
        return;
    }

    if (!isHlsStream) {
        video.src = src;
        video.muted = this.isMuted;
        video.play().catch(() => {});
    }
}

function startImageProgress() {
    const duration = (this.currentItem ? this.currentItem.duration : 5) * 1000;
    this.imageProgressStartTime = Date.now();
    this.imageProgressPausedAt = 0;

    this.progressInterval = setInterval(() => {
        const elapsed = Date.now() - this.imageProgressStartTime;
        this.progress = Math.min((elapsed / duration) * 100, 100);

        if (this.progress >= 100) {
            this.goToNextItem();
        }
    }, 50);
}

function resumeImageProgress() {
    const duration = (this.currentItem ? this.currentItem.duration : 5) * 1000;
    const remainingPercent = 100 - this.imageProgressPausedAt;
    const remainingTime = (remainingPercent / 100) * duration;
    const resumeStartTime = Date.now();

    this.progressInterval = setInterval(() => {
        const elapsed = Date.now() - resumeStartTime;
        this.progress = Math.min(this.imageProgressPausedAt + (elapsed / remainingTime) * remainingPercent, 100);

        if (this.progress >= 100) {
            this.goToNextItem();
        }
    }, 50);
}

function stopProgress() {
    if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
    }
}

function onVideoTimeUpdate() {
    const video = this.$refs.videoEl;
    if (video && video.duration) {
        this.progress = (video.currentTime / video.duration) * 100;
    }
}

function onVideoEnded() {
    this.goToNextItem();
}

function onVideoPlaying() {
    this.videoReady = true;

    const nextStoryIndex = this.findNextValidStoryIndex(this.currentStoryIndex);
    if (nextStoryIndex !== -1) {
        this.preloadStory(nextStoryIndex);
    }
}

function goToNextItem() {
    this.isPaused = false;
    this.stopProgress();

    if (this.currentItemIndex < this.totalItems - 1) {
        this.currentItemIndex++;
        this.progress = 0;
        this.$nextTick(() => {
            this.playCurrentItem();
        });
    } else {
        this.goToNextStory();
    }
}

function goToPreviousItem() {
    if (Date.now() - this.lastHoldReleaseTime < 100) {
        return;
    }

    this.isPaused = false;
    this.stopProgress();

    if (this.currentItemIndex > 0) {
        this.currentItemIndex--;
        this.progress = 0;
        this.$nextTick(() => {
            this.playCurrentItem();
        });
    } else {
        this.goToPrevStory();
    }
}

function goToNextStory() {
    if (this.isTransitioning) {
        return;
    }
    this.stopProgress();

    const nextIndex = this.findNextValidStoryIndex(this.currentStoryIndex);
    if (nextIndex === -1) {
        this.close();
        return;
    }

    this.startStoryTransition(nextIndex, 'next');
}

function goToPrevStory() {
    if (this.isTransitioning) {
        return;
    }
    this.stopProgress();

    const prevIndex = this.findPreviousValidStoryIndex(this.currentStoryIndex);
    if (prevIndex === -1) {
        this.close();
        return;
    }

    this.startStoryTransition(prevIndex, 'prev');
}

function startStoryTransition(targetIndex, direction) {
    if (this.isTransitioning) {
        return;
    }

    this.isPaused = false;
    this.stopProgress();

    const video = this.$refs.videoEl;
    if (video) {
        video.pause();
    }

    this.incomingStoryIndex = targetIndex;
    this.transitionDirection = direction;
    this.transitionPhase = 'out';
    this.isTransitioning = true;

    setTimeout(() => {
        this.completeStoryTransition();
    }, 150);
}

function completeStoryTransition() {
    if (this.incomingStoryIndex === null) {
        return;
    }

    this.currentStoryIndex = this.incomingStoryIndex;
    this.currentItemIndex = 0;
    this.progress = 0;

    this.transitionPhase = 'in';

    this.trackStoryView(this.incomingStoryIndex);

    setTimeout(() => {
        this.isTransitioning = false;
        this.transitionDirection = null;
        this.transitionPhase = null;
        this.incomingStoryIndex = null;
        this.playCurrentItem();
    }, 150);
}

function handleNavClick(direction) {
    if (this.isHolding || Date.now() - this.lastHoldReleaseTime < 100) {
        return;
    }
    if (direction === 'prev') {
        this.goToPreviousItem();
    } else {
        this.goToNextItem();
    }
}

function toggleMute() {
    this.isMuted = !this.isMuted;
    const video = this.$refs.videoEl;
    if (video) {
        video.muted = this.isMuted;
    }
}

function togglePause() {
    if (this.isPaused) {
        this.isPaused = false;
        if (this.currentItem && this.currentItem.type === 'video') {
            const video = this.$refs.videoEl;
            if (video) {
                video.play().catch(() => {});
            }
            return;
        }
        this.resumeImageProgress();
        return;
    }

    this.isPaused = true;
    if (this.currentItem && this.currentItem.type === 'video') {
        const video = this.$refs.videoEl;
        if (video) {
            video.pause();
        }
        return;
    }

    if (this.progressInterval) {
        const duration = (this.currentItem ? this.currentItem.duration : 5) * 1000;
        const elapsed = Date.now() - this.imageProgressStartTime;
        this.imageProgressPausedAt = Math.min((elapsed / duration) * 100, 100);
        this.stopProgress();
    }
}

function pauseForHold() {
    this.isHolding = true;

    if (this.currentItem && this.currentItem.type === 'video') {
        const video = this.$refs.videoEl;
        if (video) {
            video.pause();
        }
        return;
    }

    if (this.progressInterval) {
        const duration = (this.currentItem ? this.currentItem.duration : 5) * 1000;
        const elapsed = Date.now() - this.imageProgressStartTime;
        this.imageProgressPausedAt = Math.min((elapsed / duration) * 100, 100);
        this.stopProgress();
    }
}

function resumeFromHold() {
    if (!this.isHolding) {
        return;
    }
    this.isHolding = false;

    if (this.currentItem && this.currentItem.type === 'video') {
        const video = this.$refs.videoEl;
        if (video) {
            video.play().catch(() => {});
        }
    } else {
        this.resumeImageProgress();
    }
}

function onKeydown(e) {
    if (e.key === 'Escape') {
        this.close();
    } else if (e.key === 'ArrowRight') {
        this.goToNextItem();
    } else if (e.key === 'ArrowLeft') {
        this.goToPreviousItem();
    }
}

function onTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;

    if (this.holdTimeout) {
        clearTimeout(this.holdTimeout);
    }

    this.holdTimeout = setTimeout(() => {
        this.pauseForHold();
    }, 150);
}

function onTouchEnd(e) {
    if (this.holdTimeout) {
        clearTimeout(this.holdTimeout);
        this.holdTimeout = null;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - this.touchStartX;
    const deltaY = touchEndY - this.touchStartY;
    const isSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50;

    if (isSwipe) {
        e.preventDefault();
        this.isHolding = false;
        if (deltaX > 0) {
            this.goToPrevStory();
        } else {
            this.goToNextStory();
        }
        return;
    }

    if (this.isHolding) {
        e.preventDefault();
        this.resumeFromHold();
        return;
    }

    if (touchEndY < 80) {
        return;
    }

    e.preventDefault();
    const screenWidth = window.innerWidth;
    if (touchEndX < screenWidth / 2) {
        this.goToPreviousItem();
    } else {
        this.goToNextItem();
    }
}

function onMouseDown() {
    if (this.holdTimeout) {
        clearTimeout(this.holdTimeout);
    }

    this.holdTimeout = setTimeout(() => {
        this.pauseForHold();
    }, 150);
}

function onMouseUp() {
    if (this.holdTimeout) {
        clearTimeout(this.holdTimeout);
        this.holdTimeout = null;
    }

    if (this.isHolding) {
        this.lastHoldReleaseTime = Date.now();
        this.resumeFromHold();
    }
}

export default {
    name: 'StoryViewerComponent',
    mixins: [userMixin, xtrackingMixin],
    props: {
        stories: {
            type: Array,
            required: true,
        },
        initialStoryIndex: {
            type: Number,
            default: 0,
        },
    },
    data,
    mounted,
    beforeDestroy,
    computed: {
        currentStory() {
            return this.stories[this.currentStoryIndex] || null;
        },
        currentItem() {
            if (!this.currentStory) {
                return null;
            }
            return this.currentStory.items[this.currentItemIndex] || null;
        },
        totalItems() {
            return this.currentStory ? this.currentStory.items.length : 0;
        },
    },
    methods: {
        close,
        trackStoryView,
        destroyHls,
        destroyAllPreloaded,
        preloadStory,
        shouldSkipStory,
        storyHasPlaceholderItems,
        findNextValidStoryIndex,
        findPreviousValidStoryIndex,
        playCurrentItem,
        initHlsPlayer,
        startImageProgress,
        resumeImageProgress,
        stopProgress,
        onVideoTimeUpdate,
        onVideoEnded,
        onVideoPlaying,
        goToNextItem,
        goToPreviousItem,
        goToNextStory,
        goToPrevStory,
        startStoryTransition,
        completeStoryTransition,
        handleNavClick,
        toggleMute,
        togglePause,
        pauseForHold,
        resumeFromHold,
        onKeydown,
        onTouchStart,
        onTouchEnd,
        onMouseDown,
        onMouseUp,
    },
};
</script>
